// client/src/services/auth.ts
import axios from 'axios';
import config from '../config/config';

// Separate axios instance for auth calls so auth requests never pass through
// the main client's 401/refresh interceptor (avoids refresh loops).
const authApi = axios.create({
  baseURL: config.apiUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

export interface RefreshResult {
  accessToken: string;
  refreshToken: string;
  user: any;
}

const clearStoredTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
};

const storeTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('token', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  window.dispatchEvent(new Event('token:updated'));
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Single-flight token refresh.
 *
 * All callers share one in-flight request, so concurrent 401s, proactive
 * timers and multiple tabs never fire parallel refreshes against the
 * single-use rotating refresh token.
 *
 * - Sends the refresh token CURRENT in localStorage at call time.
 * - If a 401 comes back but localStorage now holds a different refresh token,
 *   another caller already rotated while we were in flight — adopt the stored
 *   pair instead of failing.
 * - 401/400 are terminal (token consumed/expired) — never retried, tokens cleared.
 * - Network/5xx get one retry, re-reading the token before the attempt;
 *   a failed retry does NOT clear tokens (a blip shouldn't log the user out —
 *   the next 401 interceptor pass will decide).
 */
let inFlightRefresh: Promise<RefreshResult> | null = null;

async function performRefresh(): Promise<RefreshResult> {
  const presentedToken = localStorage.getItem('refreshToken');
  if (!presentedToken) {
    throw new Error('No refresh token available');
  }

  for (let attempt = 0; attempt < 2; attempt++) {
    const tokenToSend = localStorage.getItem('refreshToken');
    if (!tokenToSend) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await authApi.post('/auth/refresh-token', { refreshToken: tokenToSend });
      const { accessToken, refreshToken: newRefreshToken, user } = response.data;

      if (!accessToken || !newRefreshToken) {
        throw new Error('Invalid response from server');
      }

      storeTokens(accessToken, newRefreshToken);
      return { accessToken, refreshToken: newRefreshToken, user };
    } catch (error: any) {
      const status = error.response?.status;

      // Another tab/path may have rotated the token while we were in flight —
      // if localStorage now holds a different refresh token, adopt it.
      const currentRefresh = localStorage.getItem('refreshToken');
      const currentAccess = localStorage.getItem('token');
      if (currentRefresh && currentAccess && currentRefresh !== tokenToSend) {
        return { accessToken: currentAccess, refreshToken: currentRefresh, user: null };
      }

      if (status === 401 || status === 400) {
        // Refresh token consumed/expired — terminal. Do not retry it.
        clearStoredTokens();
        throw new Error(error.response?.data?.message || 'Session expired');
      }

      // Network error / 5xx / timeout — single retry, then give up WITHOUT
      // clearing tokens so a connectivity blip doesn't log the user out.
      if (attempt === 0) {
        await delay(1000);
        continue;
      }
      throw error;
    }
  }
  throw new Error('Failed to refresh token');
}

export const refreshToken = (): Promise<RefreshResult> => {
  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
};

export const login = async (email: string, password: string) => {
  const response = await authApi.post('/auth/login', { email, password });
  const { accessToken, refreshToken: newRefreshToken } = response.data;
  storeTokens(accessToken, newRefreshToken);
  return response.data;
};

/**
 * Logout: revoke the server-side session first (best-effort), then clear local
 * state. A failed network call must still leave the browser logged out.
 */
export const logout = async (): Promise<void> => {
  const token = localStorage.getItem('token');
  try {
    if (token) {
      await authApi.post('/auth/logout', {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
    }
  } catch {
    // Server revocation is best-effort — local logout proceeds regardless
  } finally {
    clearStoredTokens();
  }
};

export const forgotPassword = async (email: string) => {
  const response = await authApi.post('/auth/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token: string, password: string) => {
  const response = await authApi.post(`/auth/reset-password/${token}`, { password });
  return response.data;
};
